/**
 * 「小芋頭服務」選單（header 右上角的九宮格）的資料來源。
 *
 * 2026-10-28 起改在管理後台「網站管理 → 九宮格管理」維護，三站讀平台 API 的 GET api/ServiceGrid。
 * **後台還沒建立或還沒從 Google Sheet 匯入之前，API 會回空的** —— 這段期間照舊讀原本那份
 * Google Sheet，上版到後台匯入之間才不會有一段空白。後台有資料之後就不會再讀 Sheet；
 * 正式站確認都匯入後，Sheet 這段與環境變數 VUE_APP_TARO_SERVICE_SHEET_ID 可以一起拿掉（見待辦）。
 *
 * 為什麼獨立成一個模組：這段邏輯原本在四個 header 元件裡各複製一份
 * （Header、Header2、JhaohoHeader、YentityHeader），四份逐字相同。
 * 改一個地方要記得改四次，而漏掉的那幾個不會壞、只會行為不一致 ——
 * 例如快取時效不同步，使用者在不同頁面看到不一樣的服務清單。
 *
 * 這裡刻意寫成**不依賴 Vue 的純函式**，元件只負責把結果放進自己的 data。
 * 社群站、AI 站各有一份同名同結構的檔案，改這裡要一起改。
 *
 * 唯一與本專案綁定的是 LOCAL_ICONS —— 它用 require() 指到本地圖檔（Google Sheet 用得到）。
 */

const LOCAL_ICONS = {
  platform_icon: require("@/assets/image/serivceIcon/platform_icon.svg"),
  sms_icon: require("@/assets/image/serivceIcon/sms_icon.svg"),
  ad_icon: require("@/assets/image/serivceIcon/ad_icon.svg"),
  ai_chat_icon: require("@/assets/image/ai/金豐ai公版icon.svg"),
  shop_icon: require("@/assets/image/home/icon_shop.svg"),
  upgrade_icon: require("@/assets/image/home/icon_upgrade.svg"),
  llh_icon: require("@/assets/image/serivceIcon/llh_icon.svg"),
  legal_icon: require("@/assets/image/serivceIcon/legal_icon.svg"),
  lecture_icon: require("@/assets/image/serivceIcon/lecture_icon.svg"),
  certificate_icon: require("@/assets/image/serivceIcon/certificate_icon.svg"),
  training_icon: require("@/assets/image/serivceIcon/training_icon.svg"),
  university_icon: require("@/assets/image/serivceIcon/university_icon.svg"),
  yentity_icon: require("@/assets/projectYentity/icon.png"),
  iso_icon: require("@/assets/image/serivceIcon/iso_icon.svg"),
  jhaoho_icon: require("@/assets/image/serivceIcon/jhaoho_icon.png"),
  hr1_icon: require("@/assets/image/serivceIcon/hr1_icon.svg"),
};

const TARO_SERVICE_CACHE_KEY = "_taroServiceCache";

/** 讀服務清單快取；沒有 / 壞掉 / 空的都當作沒快取 */
export function readServiceCache() {
  try {
    const cache = JSON.parse(localStorage.getItem(TARO_SERVICE_CACHE_KEY));
    if (!cache || !Array.isArray(cache.services) || cache.services.length === 0) return null;
    return cache;
  } catch (e) {
    return null;
  }
}

export function writeServiceCache(services) {
  try {
    localStorage.setItem(TARO_SERVICE_CACHE_KEY, JSON.stringify({ savedAt: Date.now(), services }));
  } catch (e) {
    /* 容量滿或無痕模式，寫不進去就算了，不影響顯示 */
  }
}

/**
 * 把遠端 icon 先丟進瀏覽器快取。
 * 選單是 v-menu，內容要等第一次點開才會 render，屆時才開始下載圖 ——
 * 所以在 mounted 就先預抓，使用者點開時圖已經在快取裡。
 */
export function prefetchServiceIcons(services) {
  if (!Array.isArray(services)) return;

  /*
      預渲染時不抓。

      這些 icon 放在 Google 的圖片 CDN，一次預抓整排（二十幾張）。
      真實訪客這樣做是對的 —— 服務選單一打開就有圖。但預渲染是一次烤
      二十幾頁、每頁都抓一輪，Google 會直接回 429 限流：建置日誌被幾百行
      「429」洗版，真的有問題時反而看不到，而且白白拖慢建置。

      對產出完全沒有影響 —— <img src> 照樣寫進 HTML，訪客載得到。
      預抓只是暖快取，快取在 Puppeteer 關掉的那一刻就沒了。
  */
  if (typeof window !== "undefined" && window.__PRERENDER_INJECTED && window.__PRERENDER_INJECTED.prerender) {
    return;
  }

  services.forEach((s) => {
    if (!s || !s.icon || !/^https?:\/\//.test(s.icon)) return; // 本地 require 進來的不用抓
    const img = new Image();
    img.decoding = "async";
    img.src = s.icon;
  });
}

export function parseCSV(csvText) {
  const lines = [];
  let row = [""];
  let inQuotes = false;

  for (let i = 0; i < csvText.length; i++) {
    const c = csvText[i];
    const next = csvText[i + 1];

    if (c === '"') {
      if (inQuotes && next === '"') {
        row[row.length - 1] += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (c === "," && !inQuotes) {
      row.push("");
    } else if ((c === "\r" || c === "\n") && !inQuotes) {
      if (c === "\r" && next === "\n") {
        i++;
      }
      lines.push(row);
      row = [""];
    } else {
      row[row.length - 1] += c;
    }
  }
  if (row.length > 1 || row[0] !== "") {
    lines.push(row);
  }
  return lines;
}

export function resolveIconUrl(icon) {
  if (!icon) return "";
  icon = icon.trim();

  // Normalize icon string: lowercase and remove extension (.svg, .png, etc.)
  let cleanIcon = icon.replace(/\.[a-zA-Z0-9]+$/, "").toLowerCase();

  // Direct match
  if (LOCAL_ICONS[cleanIcon]) {
    return LOCAL_ICONS[cleanIcon];
  }

  // Special mappings for existing icons
  if (cleanIcon === "金豐ai公版icon") {
    return LOCAL_ICONS["ai_chat_icon"];
  }
  if (cleanIcon === "icon") {
    return LOCAL_ICONS["yentity_icon"];
  }
  if (cleanIcon === "icon_shop") {
    return LOCAL_ICONS["shop_icon"];
  }
  if (cleanIcon === "icon_upgrade") {
    return LOCAL_ICONS["upgrade_icon"];
  }

  // Check if it's in LOCAL_ICONS directly in case of case-sensitivity
  if (LOCAL_ICONS[icon]) {
    return LOCAL_ICONS[icon];
  }

  const gdRegex = /(?:drive\.google\.com\/(?:file\/d\/|open\?id=|uc\?(?:[^#]*&)?id=|thumbnail\?(?:[^#]*&)?id=)|docs\.google\.com\/file\/d\/)([a-zA-Z0-9_-]{25,})/;
  const match = icon.match(gdRegex);
  if (match && match[1]) {
    // 直接指向 Google 圖片 CDN，不要走 drive.google.com/thumbnail。
    // 那個網址回的是 302，且標頭是 no-cache, no-store, must-revalidate ——
    // 等於每次進站每張圖都要多一趟不能被快取的來回(實測每張多 0.25 秒)。
    // lh3 這邊直接 200、Cache-Control: max-age=86400、CORS 為 *。
    // w96：icon 實際顯示 38px，96 已涵蓋 2x 高解析螢幕。
    return `https://lh3.googleusercontent.com/d/${match[1]}=w96`;
  }

  return icon;
}

export function resolveActionUrl(action) {
  if (!action) return "";
  action = action.trim();

  const match = action.match(/^(SSO|SMS|AI)(?:\/(.*))?$/i);
  if (match) {
    const platform = match[1].toUpperCase();
    const path = match[2] ? "/" + match[2] : "";

    let baseUrl = "";
    if (platform === "SSO") {
      baseUrl = process.env.VUE_APP_MAIN_BASE_URL || "";
    } else if (platform === "SMS") {
      baseUrl = process.env.VUE_APP_MAIN_SMS_BASE_URL || "";
    } else if (platform === "AI") {
      baseUrl = process.env.VUE_APP_MAIN_AICHAT_BASE_URL || "";
    }

    if (baseUrl.endsWith("/") && path.startsWith("/")) {
      return baseUrl + path.slice(1);
    }
    return baseUrl + path;
  }

  return action;
}

/** 讀原本那份 Google Sheet，解析成清單；讀不到或格式不對回空清單 */
async function fetchSheetServices(sheetId) {
  if (!sheetId) return [];

  let csvUrl = "";
  const trimmedSheetId = sheetId.trim();
  if (trimmedSheetId.startsWith("http://") || trimmedSheetId.startsWith("https://")) {
    if (trimmedSheetId.includes("output=csv")) {
      csvUrl = trimmedSheetId;
    } else {
      const match = trimmedSheetId.match(/\/d\/([a-zA-Z0-9-_/]+?)(?:\/pub|\/edit|\/htmlview|\/copy|\/preview|$)/);
      const id = match && match[1] ? match[1] : "";
      csvUrl = id ? `https://docs.google.com/spreadsheets/d/${id}/pub?output=csv` : trimmedSheetId;
    }
  } else {
    csvUrl = `https://docs.google.com/spreadsheets/d/${trimmedSheetId}/pub?output=csv`;
  }

  try {
    // no-store：Google 給這支 CSV 的是 max-age=300，不關掉的話瀏覽器
    // 五分鐘內會拿自己的舊副本，等於 localStorage 拿掉的那層快取又長回來
    const response = await fetch(csvUrl, { credentials: "omit", cache: "no-store" });
    if (!response.ok) throw new Error("Network response was not ok");
    const csvText = await response.text();
    const rows = parseCSV(csvText);
    if (rows.length <= 1) return [];

    const headers = rows[0].map((h) => h.trim().toLowerCase());
    const findHeader = (names) => {
      const idx = names.map((h) => headers.indexOf(h)).find((x) => x !== -1);
      return idx === undefined ? -1 : idx;
    };
    const nameIdx = headers.indexOf("name");
    const iconIdx = headers.indexOf("icon");
    const iconUrlIdx = findHeader(["iconurl", "icon_url", "icon url", "iconlink", "icon_link", "icon link", "imageurl", "image_url", "image url"]);
    const iconIdIdx = findHeader(["iconid", "icon_id", "icon id", "driveid", "drive_id", "drive id", "fileid", "file_id", "file id"]);
    const urlIdx = headers.indexOf("url") !== -1 ? headers.indexOf("url") : headers.indexOf("action");
    const targetIdx = headers.indexOf("target");

    if (nameIdx === -1 || (iconIdx === -1 && iconUrlIdx === -1 && iconIdIdx === -1) || urlIdx === -1) {
      console.error("Google Sheet headers must contain: name, icon/iconUrl/iconId, url (or action)");
      return [];
    }

    const services = [];

    for (let i = 1; i < rows.length; i++) {
      const row = rows[i];
      if (row.length === 0) continue;

      const name = row[nameIdx] ? row[nameIdx].trim() : "";
      if (!name) continue;

      let rawIcon = iconUrlIdx !== -1 && row[iconUrlIdx] ? row[iconUrlIdx].trim() : "";
      if (!rawIcon && iconIdx !== -1 && row[iconIdx]) rawIcon = row[iconIdx].trim();
      if (!rawIcon && iconIdIdx !== -1 && row[iconIdIdx]) rawIcon = `https://drive.google.com/file/d/${row[iconIdIdx].trim()}/view`;
      const icon = resolveIconUrl(rawIcon);

      const rawUrl = row[urlIdx] ? row[urlIdx].trim() : "";
      const action = resolveActionUrl(rawUrl);

      const target = targetIdx !== -1 && row[targetIdx] ? row[targetIdx].trim() : "_self";

      services.push({ name, icon, action, target });
    }

    return services;
  } catch (err) {
    console.error("讀不到 Google Sheet 的服務清單：", err);
    return [];
  }
}

/**
 * 後台維護的名稱是純文字、換行是 \n。header 用 v-html 顯示（要換行），所以先跳脫再把 \n 換成 <br>，
 * 名稱裡就算有 < > 也只會顯示成文字。（Google Sheet 的名稱本來就是 HTML，照舊直接用）
 */
export function toNameHtml(name) {
  return String(name || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
    .replace(/\r?\n/g, "<br>");
}

/** 平台 API 的基底（…/api/）→ 九宮格清單的網址 */
function serviceGridUrl(apiBaseUrl) {
  let base = String(apiBaseUrl || "").trim().replace(/\/+$/, "");
  if (!base) return "";
  if (!/\/api$/i.test(base)) base += "/api";
  return base + "/ServiceGrid";
}

/** 讀後台維護的九宮格；HTTP 錯誤會丟例外，後台還沒資料時回空清單 */
async function fetchApiServices(apiBaseUrl) {
  const url = serviceGridUrl(apiBaseUrl);
  if (!url) return [];
  // 不帶 cookie：這支是匿名的。no-store：每次都要拿到後台最新的設定
  const response = await fetch(url, { credentials: "omit", cache: "no-store" });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const rows = await response.json();
  if (!Array.isArray(rows)) throw new Error("九宮格清單的格式不對");
  return rows.map((x) => ({
    name: toNameHtml(x.name),
    icon: x.icon || "",
    action: resolveActionUrl(x.url),
    target: x.target === "_blank" ? "_blank" : "_self",
  }));
}

export async function loadTaroServices({ apiBaseUrl, sheetId, onServices, onLoading } = {}) {
  const emit = typeof onServices === "function" ? onServices : () => {};
  const setLoading = typeof onLoading === "function" ? onLoading : () => {};

  /*
      快取只負責「先把畫面畫出來」，不負責決定要不要重抓（stale-while-revalidate）：
      快取先上畫面（所以不會閃骨架屏），同時一定去抓一次新的，內容不一樣就換掉。
  */
  const cache = readServiceCache();
  if (cache) {
    emit(cache.services);
    prefetchServiceIcons(cache.services);
  } else {
    setLoading(true); // 只有真的沒東西可顯示時才鋪骨架屏
  }

  try {
    let services = [];
    try {
      services = await fetchApiServices(apiBaseUrl || process.env.VUE_APP_MAIN_API_BASE_URL);
    } catch (err) {
      // 平台 API 還沒上新版（404）或暫時不通：改讀 Google Sheet
      console.warn("讀不到後台的九宮格，改讀 Google Sheet：", err);
    }

    // 後台還沒有資料（還沒新增、還沒從 Google Sheet 匯入）：照舊讀 Sheet，不要讓九宮格空掉
    if (!services.length) {
      services = await fetchSheetServices(sheetId || process.env.VUE_APP_TARO_SERVICE_SHEET_ID);
    }
    if (!services.length) return; // 兩邊都沒有：維持快取（或空的）

    writeServiceCache(services);

    /*
        內容沒變就不要再 emit 一次。

        每次進站都會走到這裡，無條件 emit 等於每次都把整份清單換掉；
        選單剛好開著的話會整塊重畫，圖示也會閃一下。
    */
    const changed = !cache || JSON.stringify(cache.services) !== JSON.stringify(services);
    if (changed) {
      emit(services);
      prefetchServiceIcons(services);
    }
  } catch (err) {
    // 不讓整個 header 跟著壞；上面已經 emit 過快取
    console.error("讀不到小芋頭服務清單：", err);
  } finally {
    setLoading(false);
  }
}
